import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Slider,
  Paper,
  Stack,
  IconButton,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment
} from '@mui/material';
import { styled } from '@mui/system';
import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { supabase } from '../../supabaseClient';


const OptionsPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(2),
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(30, 30, 45, 0.95)' : 'rgba(255, 255, 255, 0.95)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
  backdropFilter: 'blur(10px)',
  width: '100%',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
  animation: 'fadeIn 0.3s ease-out',
  maxHeight: '80vh',
  overflowY: 'auto',
  '@keyframes fadeIn': {
    '0%': {
      opacity: 0,
      transform: 'translateY(10px)'
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)'
    }
  }
}));

const PreviewBox = styled(Box)(({ theme }) => ({
  borderRadius: theme.spacing(1.5),
  marginBottom: theme.spacing(1.5),
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.05)',
  minHeight: 120,
  position: 'relative',
  overflow: 'hidden',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
}));

const OptionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.primary.main,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  '& svg': {
    fontSize: '1.2rem'
  }
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: 8,
  right: 8,
  padding: 4,
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.05)',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.1)',
  },
  zIndex: 10 // Ensure button is above other elements
}));

const LogoToolOptions = ({ settings, onChange, onApply, currentImage }) => {
  const [userLogos, setUserLogos] = useState([]);
  const [loading, setLoading] = useState(true); // Loading for initial logo fetch
  // Removed logo upload state: logoUploadLoading, newLogoFile, logoName, logoDialogOpen
  const [previewStyle, setPreviewStyle] = useState({});
  const [selectedLogo, setSelectedLogo] = useState(null);
  const [session, setSession] = useState(null); // Need session for user ID

  // Get session on mount
  useEffect(() => {
    const getSession = async () => {
      const { data } = await supabase.auth.getSession();
      setSession(data.session);
    };
    getSession();
  }, []);

  useEffect(() => {
    if (session?.user?.id) {
      fetchUserLogos();
    } else {
      // Handle case where user is not logged in, maybe disable logo features
      setLoading(false);
      setUserLogos([]);
    }
  }, [session]);
  
  // Update preview when settings change
  useEffect(() => {
    updatePreview();
  }, [settings, currentImage, selectedLogo]);
  
  const fetchUserLogos = async () => {
    try {
      if (!session?.user?.id) {
        console.log("No user session found, skipping logo fetch.");
        setUserLogos([]);
        setLoading(false);
        return;
      }
      setLoading(true);

      // Fetch logos from database using the session state
      const { data, error } = await supabase
        .from('user_logos')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      
      setUserLogos(data || []);
    } catch (error) {
      console.error('Logolar yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const updatePreview = () => {
    // We're not using previewStyle anymore since we're directly applying styles in the JSX
    // This function is kept for compatibility but doesn't need to do anything
    console.log("Logo preview updated with:", {
      position: settings.position,
      size: settings.size,
      opacity: settings.opacity,
      logoUrl: selectedLogo?.logo_url
    });
  };
  
  const handleLogoSelect = (logo) => {
    setSelectedLogo(logo);
    onChange({
      ...settings,
      logoUrl: logo.logo_url,
      logoId: logo.id
    });
  };
  
  const handlePositionChange = (position) => {
    onChange({ ...settings, position });
  };
  
  const handleSizeChange = (event, newValue) => {
    onChange({ ...settings, size: newValue });
  };
  
  const handleOpacityChange = (event, newValue) => {
    onChange({ ...settings, opacity: newValue });
  };
  
  const handleReset = () => {
    onChange({
      position: 'bottom-right',
      size: 20,
      opacity: 80,
      logoUrl: null,
      logoId: null
    });
    setSelectedLogo(null);
  };

  // Removed handleLogoFileChange, uploadLogo, handleSaveLogo

  // Dispatch event to open profile modal/dialog
  const handleAddNewLogoClick = () => {
    // Dispatch an event that can be caught by the parent component (ImageEditor -> App)
    // to open the EditProfile dialog with the Logos tab selected.
    const event = new CustomEvent('openProfileDialog', {
      detail: { tabIndex: 1 } // 1 is the index for the Logos tab
    });
    window.dispatchEvent(event);
    // Close the tool options panel
    onApply(false);
  };
  
  return (
    <OptionsPaper elevation={4}>
      <Box sx={{ position: 'relative' }}>
        <CloseButton onClick={() => onApply(false)}>
          <CloseIcon fontSize="small" />
        </CloseButton>
        
        <Stack spacing={1.5}>
          <Box>
            <OptionTitle>
              <BrandingWatermarkIcon /> Logo Ekle
            </OptionTitle>
          </Box>
          
          <PreviewBox>
            <Box 
              sx={{ 
                width: '100%', 
                height: '100%',
                backgroundImage: currentImage ? `url(${currentImage})` : 'none',
                backgroundSize: 'contain',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                position: 'relative'
              }}
            >
              {selectedLogo && (
                <Box 
                  sx={{
                    position: 'absolute',
                    width: `${settings.size}%`,
                    maxWidth: '80%',
                    height: '40px',
                    opacity: settings.opacity / 100,
                    backgroundImage: `url(${selectedLogo.logo_url})`,
                    backgroundSize: 'contain',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    transition: 'all 0.3s ease',
                    ...(settings.position === 'top-left' && { top: '10%', left: '10%' }),
                    ...(settings.position === 'top-right' && { top: '10%', right: '10%' }),
                    ...(settings.position === 'bottom-left' && { bottom: '10%', left: '10%' }),
                    ...(settings.position === 'bottom-right' && { bottom: '10%', right: '10%' }),
                  }}
                />
              )}
            </Box>
          </PreviewBox>
          
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Logonuzu Seçin
          </Typography>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={30} />
            </Box>
          ) : userLogos.length === 0 ? (
            <Box sx={{ 
              textAlign: 'center', 
              py: 2, 
              backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
              borderRadius: '8px',
              border: theme => `1px dashed ${theme.palette.divider}`
            }}>
              <Typography color="text.secondary" variant="body2" sx={{ mb: 1 }}>
                Henüz logo yüklemediniz
              </Typography>
              <Button 
                startIcon={<AddIcon />} 
                variant="outlined"
                size="small"
                onClick={handleAddNewLogoClick} // Now triggers profile modal
              >
                Logo Eklemek İçin Tıklayın
              </Button>
            </Box>
          ) : (
            <>
              <Grid container spacing={1} sx={{ mb: 1 }}>
                {userLogos.map(logo => (
                  <Grid item xs={4} key={logo.id}>
                    <Card 
                      sx={{ 
                        cursor: 'pointer',
                        border: theme => selectedLogo?.id === logo.id ? 
                          `2px solid ${theme.palette.primary.main}` : 
                          `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                        transition: 'all 0.2s',
                        '&:hover': {
                          boxShadow: 2,
                          transform: 'translateY(-2px)'
                        }
                      }}
                      onClick={() => handleLogoSelect(logo)}
                    >
                      <Box sx={{ 
                        height: 60, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        p: 1,
                        backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.03)'
                      }}>
                        <CardMedia
                          component="img"
                          image={logo.logo_url}
                          alt={logo.logo_name || "Logo"}
                          sx={{ 
                            maxHeight: '100%', 
                            width: 'auto', 
                            maxWidth: '100%',
                            objectFit: 'contain'
                          }}
                        />
                      </Box>
                    </Card>
                  </Grid>
                ))}
                {/* Removed the "Add New Logo" card from the grid */}
              </Grid>
              {/* Button to add/manage logos, always visible when logos exist */}
              <Button 
                startIcon={<AddIcon />} 
                variant="outlined"
                size="small"
                onClick={handleAddNewLogoClick}
                sx={{ mt: 1.5, width: '100%' }} // Add margin top and make full width
              >
                Logo Ekle / Yönet
              </Button>
            </>
          )}
          
          {selectedLogo && (
            <>
              <Box>
                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                  Logo Konumu
                </Typography>
                
                <Box sx={{ 
                  backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.03)',
                  borderRadius: 1.5,
                  p: 1.5,
                  position: 'relative',
                  height: 120,
                  border: theme => `1px solid ${theme.palette.divider}`,
                  mb: 1
                }}>
                  {/* Visual representation of the image */}
                  <Box sx={{ 
                    position: 'absolute', 
                    top: '50%', 
                    left: '50%', 
                    transform: 'translate(-50%, -50%)',
                    width: '70%',
                    height: '70%',
                    border: '1px dashed #888',
                    borderRadius: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                    <Typography variant="caption" color="text.secondary">Görsel</Typography>
                  </Box>
                  
                  {/* Position buttons */}
                  {[
                    { pos: 'top-left', label: 'Sol Üst', x: 10, y: 10 },
                    { pos: 'top-right', label: 'Sağ Üst', x: 'calc(100% - 40px)', y: 10 },
                    { pos: 'bottom-left', label: 'Sol Alt', x: 10, y: 'calc(100% - 40px)' },
                    { pos: 'bottom-right', label: 'Sağ Alt', x: 'calc(100% - 40px)', y: 'calc(100% - 40px)' }
                  ].map((item) => (
                    <Tooltip title={item.label} key={item.pos}>
                      <Box 
                        onClick={() => handlePositionChange(item.pos)}
                        sx={{
                          position: 'absolute',
                          left: item.x,
                          top: item.y,
                          width: 30,
                          height: 30,
                          border: theme => `2px solid ${settings.position === item.pos ? theme.palette.primary.main : theme.palette.divider}`,
                          borderRadius: 1,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          cursor: 'pointer',
                          backgroundColor: settings.position === item.pos ? 
                            theme => theme.palette.mode === 'dark' ? 'rgba(138, 43, 226, 0.3)' : 'rgba(138, 43, 226, 0.2)' : 
                            theme => theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.4)' : 'rgba(255, 255, 255, 0.7)',
                          '&:hover': {
                            backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(138, 43, 226, 0.25)' : 'rgba(138, 43, 226, 0.15)',
                            transform: 'scale(1.05)'
                          },
                          transition: 'all 0.2s ease',
                          zIndex: 5
                        }}
                      >
                        <Box 
                          sx={{ 
                            width: 10, 
                            height: 10, 
                            borderRadius: '50%',
                            backgroundColor: settings.position === item.pos ? 
                              theme => theme.palette.primary.main : 
                              theme => theme.palette.text.secondary
                          }} 
                        />
                      </Box>
                    </Tooltip>
                  ))}
                </Box>
                
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mb: 1 }}>
                  Seçili konum: {
                    settings.position === 'top-left' ? 'Sol Üst' :
                    settings.position === 'top-right' ? 'Sağ Üst' :
                    settings.position === 'bottom-left' ? 'Sol Alt' : 'Sağ Alt'
                  }
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, display: 'flex', alignItems: 'center' }}>
                  Boyut: {settings.size}%
                </Typography>
                <Slider
                  value={settings.size}
                  onChange={handleSizeChange}
                  min={5}
                  max={40}
                  step={1}
                  marks={[
                    { value: 5, label: '5%' },
                    { value: 20, label: '20%' },
                    { value: 40, label: '40%' },
                  ]}
                  sx={{
                    '& .MuiSlider-thumb': {
                      boxShadow: '0 0 0 8px rgba(25, 118, 210, 0.1)'
                    }
                  }}
                />
              </Box>
              
              <Box>
                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, display: 'flex', alignItems: 'center' }}>
                  Şeffaflık: {settings.opacity}%
                </Typography>
                <Slider
                  value={settings.opacity}
                  onChange={handleOpacityChange}
                  min={10}
                  max={100}
                  step={1}
                  marks={[
                    { value: 10, label: '10%' },
                    { value: 50, label: '50%' },
                    { value: 100, label: '100%' },
                  ]}
                  sx={{
                    '& .MuiSlider-thumb': {
                      boxShadow: '0 0 0 8px rgba(25, 118, 210, 0.1)'
                    }
                  }}
                />
              </Box>
            </>
          )}
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, gap: 2 }}>
            <Button 
              variant="outlined"
              onClick={handleReset}
              startIcon={<RestartAltIcon />}
              fullWidth
            >
              Sıfırla
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => onApply(true)}
              fullWidth
              disabled={!selectedLogo}
            >
              Uygula
            </Button>
          </Box>
        </Stack>
      </Box>

      {/* Removed Logo Upload/Naming Dialog */}
    </OptionsPaper>
  );
};

export default LogoToolOptions;
